import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { DataSource } from "../../types/grid.ts";
import type { ChartSettings } from "../../types/chart.ts";
import { parseTemplateColumn, IDSFormatterShim, resolveLayoutProperties, stripHtml } from "../../util/chartUtils.ts";
import { AssistantAvatar } from "../ui/assistantAvatar.tsx";
import { useAssistant } from "../../config/assistantProfile.ts";
import {
  DonutChart,
  BarChart,
  StackedBarChart,
  LineChart,
  getColor,
  type DonutSlice,
  type BarDatum,
  type StackedBarDatum,
  type LineSeries,
  type LineAnnotation,
} from "../chart/charts.tsx";
import styles from "./dataChart.module.scss";

/**
 * Format a cell value as a short chart label.
 * Handles Date objects (from gridDataParser's DateTime_Ticks/UtcTicks)
 * and ISO 8601 strings. Trims to minute precision, keeps timezone.
 *
 * Date object                          → "2025-03-15T10:30+01:00"
 * "2025-03-15T10:30:00.0000000+01:00" → "2025-03-15T10:30+01:00"
 * "2025-03-15"                         → "2025-03-15"
 */
function formatLabel(value: unknown): string {
  if (value == null) return "";

  // Date objects from binary parser — format as ISO trimmed to minutes
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return "";
    const pad = (n: number) => String(n).padStart(2, "0");
    const y = value.getFullYear();
    const mo = pad(value.getMonth() + 1);
    const d = pad(value.getDate());
    const h = pad(value.getHours());
    const mi = pad(value.getMinutes());
    const offset = -value.getTimezoneOffset();
    const sign = offset >= 0 ? "+" : "-";
    const oh = pad(Math.floor(Math.abs(offset) / 60));
    const om = pad(Math.abs(offset) % 60);
    const tz = `${sign}${oh}:${om}`;
    if (h === "00" && mi === "00") return `${y}-${mo}-${d}`;
    return `${y}-${mo}-${d}T${h}:${mi}${tz}`;
  }

  // String values — trim ISO 8601 to minutes
  const label = String(value);
  const m = label.match(/^(\d{4}-\d{2}-\d{2})(?:T(\d{2}:\d{2})(?::\d{2}(?:\.\d+)?)?(Z|[+-]\d{2}:\d{2})?)?/);
  if (!m) return label;
  const date = m[1]!;
  const time = m[2];
  const tz = m[3] ?? "";
  if (!time) return date;
  return `${date}T${time}${tz}`;
}

interface DataChartProps {
  dataSource: DataSource;
  chartConfig: ChartSettings;
  page: number;
  pageSize: number;
  totalCount: number | null;
  onPageChange: (page: number) => void;
  /** Short human title used in the "Explain" prompt to Issa. */
  chartTitle?: string;
  /** When true, the built-in Explain button is hidden (caller renders it externally). */
  hideExplainButton?: boolean;
  /** Vertical time-marker annotations for line/area charts. */
  annotations?: LineAnnotation[];
  /** Emitted once the chart's time-axis bounds and series identities are known. */
  onChartMeta?: (meta: ChartMeta) => void;
}

/** Summary of a rendered time-series chart, used by callers to align external overlays. */
export interface ChartMeta {
  /** Earliest x-axis instant (epoch ms), or null when labels aren't time-parseable. */
  minTimeMs: number | null;
  /** Latest x-axis instant (epoch ms), or null. */
  maxTimeMs: number | null;
  /** Per-series identity: numeric id parsed from the pivot key/name, raw pivot key, legend name, plus its color. */
  series: { id: number | null; key: string | null; name: string; color: string }[];
}

interface SeriesInfo {
  name: string;
  color: string;
  columnIndex: number;
  /** Raw pivot key (when PIVOT) — used to recover the series' numeric id. */
  key?: string;
  tooltipFn?: (row: unknown[]) => string;
  valueLabelFn?: (row: unknown[]) => string;
}

/** Extract the first signed integer found in any of the given strings. */
function parseFirstInt(...vals: (string | undefined)[]): number | null {
  for (const v of vals) {
    if (!v) continue;
    const m = v.match(/-?\d+/);
    if (m) return parseInt(m[0], 10);
  }
  return null;
}

/** Build the Issa explain prompt for a chart. Exported so callers can render the button externally. */
export function buildChartExplainPrompt(dataSource: DataSource, chartConfig: ChartSettings, chartTitle?: string): string {
  const chartType = (chartConfig.layout.type ?? "line").toLowerCase();
  const title = chartTitle ?? "chart";
  const headers = dataSource.attributes.header.join(", ");
  const preview = dataSource.matrix.slice(0, 8).map(r => r.map(v => v instanceof Date ? v.toISOString() : String(v)).join(" | ")).join("\n");
  return `Explain this ${chartType} chart: "${title}".\nColumns: ${headers}\nFirst rows:\n${preview}`;
}

/**
 * Renders grid DataSource data as a chart, driven by ChartSettings
 * parsed from the page's ClientOptions "IsChart:" attribute.
 *
 * Mirrors WebAdmin2's InnerChart.PrepareData() logic:
 * - Supports the PIVOT method (one series per pivot column)
 * - Maps Webix chart types to SVG chart components
 */
export function DataChart({ dataSource, chartConfig, page, pageSize, totalCount, onPageChange, chartTitle, hideExplainButton, annotations, onChartMeta }: DataChartProps) {
  const navigate = useNavigate();
  const assistant = useAssistant();
  const { layout, config } = chartConfig;
  const showLabels = chartConfig.showLabels;
  const chartType = (layout.type ?? "line").toLowerCase();
  const [containerWidth, setContainerWidth] = useState(700);
  const containerRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;
    const measure = () => {
      const w = node.clientWidth - 32; // subtract padding
      if (w > 100) setContainerWidth(w);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  const chartData = useMemo(() => {
    if (!dataSource.matrix.length) return null;

    const headers = dataSource.attributes.header;
    const indexOfHeader = (name: string) => {
      const upper = name.toUpperCase().replace(/^\[|\]$/g, "");
      return headers.findIndex((h) => h.toUpperCase().replace(/^\[|\]$/g, "") === upper);
    };

    // Resolve __:eval: and __:#: prefixed layout properties
    const resolvedLayout = JSON.parse(JSON.stringify(layout));
    resolveLayoutProperties(resolvedLayout, indexOfHeader);

    // Determine X-axis column: prefer resolved xAxis.value (column index), then template, then auto-detect
    const xAxisConf = resolvedLayout.xAxis as Record<string, unknown> | undefined;
    let xAxisIndex = -1;
    let xAxisLabelFn: ((row: unknown[]) => string) | null = null;

    // xAxis.value may be a resolved column index (from __:#:ColName)
    if (xAxisConf?.value != null && typeof xAxisConf.value === "number") {
      xAxisIndex = xAxisConf.value;
    }
    // xAxis.template may be a string with #ColName# or a resolved function (from __:eval:)
    if (xAxisConf?.template != null) {
      if (typeof xAxisConf.template === "function") {
        xAxisLabelFn = xAxisConf.template as (row: unknown[]) => string;
      } else if (typeof xAxisConf.template === "string") {
        const col = parseTemplateColumn(xAxisConf.template);
        if (col && xAxisIndex < 0) xAxisIndex = indexOfHeader(col);
      }
    }

    // Auto-detect x-axis from first Date/string column if not found
    if (xAxisIndex < 0) {
      const sampleRow = dataSource.matrix[0];
      if (sampleRow) {
        for (let i = 0; i < sampleRow.length; i++) {
          const v = sampleRow[i];
          if (v instanceof Date || (typeof v === "string" && isNaN(Number(v)))) {
            xAxisIndex = i;
            break;
          }
        }
      }
      if (xAxisIndex < 0) xAxisIndex = 0;
    }

    // Reverse matrix (WebAdmin2 does this)
    const rows = [...dataSource.matrix].reverse();

    // Build series based on method
    const method = (config.method ?? "").toUpperCase();
    const series: SeriesInfo[] = [];

    // PIVOT: one series per pivot column — mirrors WebAdmin2's InnerChart.PrepareData()
    const pivotCols = method === "PIVOT"
      ? (dataSource.attributes.pivotColumnDataType
        ?? dataSource.attributes.pivotGeneratedColumns?.map((v) => ({ Value: v })))
      : undefined;

    if (pivotCols && pivotCols.length > 0) {
      for (let ix = 0; ix < pivotCols.length; ix++) {
        const key = pivotCols[ix]!.Value;
        const position = indexOfHeader(key);
        if (position < 0) continue;

        // Apply legendFormat if provided (mirrors WebAdmin2's template substitution)
        let legendText = key;
        if (config.legendFormat) {
          legendText = applyLegendFormat(config.legendFormat, key, position, headers, indexOfHeader);
        }

        // Build tooltip function if tooltipFormat provided
        let tooltipFn: ((row: unknown[]) => string) | undefined;
        if (config.tooltipFormat) {
          tooltipFn = buildTooltipFn(config.tooltipFormat, key, position, headers, indexOfHeader);
        }

        // Build value label function if valueLabelFormat provided
        let valueLabelFn: ((row: unknown[]) => string) | undefined;
        if (config.valueLabelFormat) {
          valueLabelFn = buildTooltipFn(config.valueLabelFormat, key, position, headers, indexOfHeader);
        }

        series.push({
          name: legendText,
          color: getColor(ix),
          columnIndex: position,
          key,
          tooltipFn,
          valueLabelFn,
        });
      }
    } else {
      // Non-pivot fallback: use all numeric-looking columns except the X-axis column
      for (let i = 0; i < headers.length; i++) {
        if (i === xAxisIndex) continue;
        // Check if column has numeric data by sampling first row (exclude Date objects)
        const sample = rows[0]?.[i];
        if (sample !== null && sample !== undefined && !(sample instanceof Date) && !isNaN(Number(sample))) {
          series.push({
            name: headers[i]!,
            color: getColor(series.length),
            columnIndex: i,
          });
        }
      }
    }

    if (series.length === 0) return null;

    // Extract X-axis labels: use resolved xAxis.template function if available, else format dates
    const labels = rows.map((row) => {
      if (xAxisLabelFn) {
        try {
          const result = xAxisLabelFn(row);
          if (typeof result === "string" && result !== "") return result;
        } catch { /* fall through */ }
      }
      return formatLabel(row[xAxisIndex]);
    });

    return { rows, labels, series, xAxisIndex };
  }, [dataSource, layout, config]);

  // Emit time-axis bounds + per-series identity once the chart data is known, so
  // external overlays (e.g. door-open annotations) can align to the same window.
  const onChartMetaRef = useRef(onChartMeta);
  onChartMetaRef.current = onChartMeta;
  useEffect(() => {
    const cb = onChartMetaRef.current;
    if (!cb || !chartData) return;
    let minT: number | null = null;
    let maxT: number | null = null;
    for (const l of chartData.labels) {
      const t = l ? Date.parse(l) : NaN;
      if (Number.isNaN(t)) continue;
      if (minT == null || t < minT) minT = t;
      if (maxT == null || t > maxT) maxT = t;
    }
    cb({
      minTimeMs: minT,
      maxTimeMs: maxT,
      series: chartData.series.map((s) => ({ id: parseFirstInt(s.key, s.name), key: s.key ?? null, name: s.name, color: s.color })),
    });
  }, [chartData]);

  // Legend visibility: initially show first 10 series, user can toggle
  const [hiddenSeries, setHiddenSeries] = useState<Set<number>>(() => {
    const hidden = new Set<number>();
    for (let i = 10; i < (chartData?.series.length ?? 0); i++) hidden.add(i);
    return hidden;
  });
  const [legendSearch, setLegendSearch] = useState("");

  // Determine total — use server value if available, otherwise remember last known
  const lastKnownTotalRef = useRef<number | null>(null);

  if (!chartData) {
    return <div className={styles.empty}>No chart data available</div>;
  }

  const { rows, labels, series } = chartData;

  const toggleSeries = (idx: number) => {
    setHiddenSeries((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  // Filter series for chart rendering — only visible ones
  const visibleSeries = series.filter((_, i) => !hiddenSeries.has(i));

  // Render the appropriate chart type with visible series only
  const chart = renderChart(chartType, rows, labels, visibleSeries, containerWidth, showLabels, annotations);

  // Determine total — use server value if available, otherwise remember last known
  const serverTotal = totalCount ?? (dataSource.totalCount > 0 ? dataSource.totalCount : null);
  if (serverTotal != null) {
    lastKnownTotalRef.current = serverTotal;
  }
  const effectiveTotal = serverTotal ?? lastKnownTotalRef.current;

  // Show arrows when total exceeds page size, or when total is unknown but current page is full
  const currentPageFull = rows.length >= pageSize;
  const hasPaging = effectiveTotal != null ? effectiveTotal > pageSize : currentPageFull;
  const totalPages = effectiveTotal != null ? Math.max(1, Math.ceil(effectiveTotal / pageSize)) : null;
  const canGoNext = totalPages != null ? page < totalPages - 1 : currentPageFull;
  const canGoPrev = page > 0;

  const hasLegend = series.length > 1;

  function explainWithIssa() {
    const title = chartTitle ?? "chart";
    const headers = dataSource.attributes.header.join(", ");
    const preview = dataSource.matrix.slice(0, 8).map(r => r.map(v => v instanceof Date ? v.toISOString() : String(v)).join(" | ")).join("\n");
    const prompt = `Explain this ${chartType} chart: "${title}".\nColumns: ${headers}\nFirst rows:\n${preview}`;
    navigate("/page/issainsights", { state: { prompt } });
  }

  return (
    <div className={styles.chartOuter}>
      {!hideExplainButton && (
        <button
          type="button"
          className={styles.explainBtn}
          onClick={explainWithIssa}
          title={`Ask ${assistant.name} to explain this chart`}
          aria-label={`Ask ${assistant.name} to explain this chart`}
        >
          <AssistantAvatar size={14} />
          Explain
        </button>
      )}
      <div className={styles.chartBody}>
        <div className={styles.chartMain}>
          <div className={styles.chartRow}>
            {hasPaging && (
              <button
                className={styles.pagerBtn}
                disabled={!canGoNext}
                onClick={() => onPageChange(page + 1)}
                title="Next (older)"
              >
                ◀
              </button>
            )}
            <div className={styles.chartContainer} ref={containerRef}>
              {chart}
            </div>
            {hasPaging && (
              <button
                className={styles.pagerBtn}
                disabled={!canGoPrev}
                onClick={() => onPageChange(page - 1)}
                title="Previous (newer)"
              >
                ▶
              </button>
            )}
          </div>
        </div>
        {hasLegend && (
          <div className={styles.legend}>
            {series.length > 10 && (
              <input
                className={styles.legendSearch}
                type="text"
                placeholder="Filter series..."
                value={legendSearch}
                onChange={(e) => setLegendSearch(e.target.value)}
              />
            )}
            <div className={styles.legendList}>
              {series.map((s, i) => {
                if (legendSearch && !s.name.toLowerCase().includes(legendSearch.toLowerCase())) return null;
                const hidden = hiddenSeries.has(i);
                return (
                  <div
                    key={i}
                    className={`${styles.legendItem} ${hidden ? styles.legendItemHidden : ""}`}
                    onClick={() => toggleSeries(i)}
                    title={hidden ? "Click to show" : "Click to hide"}
                  >
                    <span
                      className={styles.legendSwatch}
                      style={{ backgroundColor: hidden ? "transparent" : s.color, borderColor: s.color }}
                    />
                    <span className={styles.legendName} title={s.name}>{s.name}</span>
                  </div>
                );
              })}
            </div>
            {series.length > 10 && (
              <div className={styles.legendActions}>
                <button className={styles.legendActionBtn} onClick={() => setHiddenSeries(new Set())}>Show all</button>
                <button className={styles.legendActionBtn} onClick={() => {
                  const all = new Set<number>();
                  for (let i = 0; i < series.length; i++) all.add(i);
                  setHiddenSeries(all);
                }}>Hide all</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Resolve #VAR# template references in format strings.
 * Mirrors WebAdmin2's regex replacement:
 * - #!IX# → column index (position)
 * - #!KEY# → column name (key)
 * - #columnName# → indexOfHeader(columnName)
 */
function resolveTemplateRefs(
  template: string,
  key: string,
  position: number,
  indexOfHeader: (name: string) => number,
): string {
  return template.replace(/([^\\]|^)#(.+?[^\\])#/gi, (_match, prefix: string, varName: string) => {
    if (varName.startsWith("!")) {
      switch (varName.toUpperCase()) {
        case "!IX": return prefix + String(position);
        case "!KEY": return prefix + key;
      }
    }
    return prefix + String(indexOfHeader(varName));
  });
}

/**
 * Apply legendFormat to produce the display name for a pivot series.
 * The format is a JS expression that may reference #!KEY#, #!IX#, and column indices.
 */
function applyLegendFormat(
  format: string,
  key: string,
  position: number,
  _headers: string[],
  indexOfHeader: (name: string) => number,
): string {
  try {
    const body = resolveTemplateRefs(format, key, position, indexOfHeader);
    const wrapped = "var IDS = arguments[0];var o = arguments[1];" + (/return/i.test(body) ? "" : "return ") + body + ";";
    return String(new Function(wrapped)(IDSFormatterShim, key));
  } catch (e) {
    console.error("Failed to parse legendFormat:", e);
    return key;
  }
}

/**
 * Build a tooltip function from tooltipFormat.
 * The format is a JS expression that receives (attributes-like object, row data array).
 * Template references #columnName# are replaced with column indices so the expression
 * can access row[index] directly.
 */
function buildTooltipFn(
  format: string,
  key: string,
  position: number,
  headers: string[],
  indexOfHeader: (name: string) => number,
): (row: unknown[]) => string {
  try {
    const body = resolveTemplateRefs(format, key, position, indexOfHeader);
    const wrapped = "var IDS = arguments[0];var a = arguments[1];var aioh = a.indexOfHeader.bind(a);var o = arguments[2];" +
      (/return/i.test(body) ? "" : "return ") + body + ";";
    const attrs = { header: headers, indexOfHeader };
    const fn = new Function(wrapped);
    return (row: unknown[]) => {
      try {
        const raw = String(fn(IDSFormatterShim, attrs, row));
        return stripHtml(raw);
      } catch {
        return String(row[position] ?? "");
      }
    };
  } catch (e) {
    console.error("Failed to parse format expression:", e);
    return (row: unknown[]) => String(row[position] ?? "");
  }
}

function toNumber(value: unknown): number {
  if (value === null || value === undefined || value === "") return 0;
  const n = Number(value);
  return isNaN(n) ? 0 : n;
}

function renderChart(
  type: string,
  rows: unknown[][],
  labels: string[],
  series: SeriesInfo[],
  width: number,
  showLabels?: boolean,
  annotations?: LineAnnotation[],
): React.ReactElement | null {
  switch (type) {
    case "line":
    case "spline":
      return renderLineChart(rows, labels, series, false, width, showLabels, annotations);

    case "area":
    case "stackedarea":
      return renderLineChart(rows, labels, series, true, width, showLabels, annotations);

    case "bar":
      if (series.length === 1) {
        return renderBarChart(rows, labels, series[0]!, width);
      }
      return renderStackedBarChart(rows, labels, series, width);

    case "stackedbar":
      return renderStackedBarChart(rows, labels, series, width);

    case "barh":
    case "stackedbarh":
      if (series.length === 1) {
        return renderBarChart(rows, labels, series[0]!, width);
      }
      return renderStackedBarChart(rows, labels, series, width);

    case "pie":
    case "pie3d":
      return renderDonutChart(rows, labels, series, 0);

    case "donut":
      return renderDonutChart(rows, labels, series, 60);

    case "scatter":
    case "radar":
    default:
      return renderLineChart(rows, labels, series, false, width, showLabels, annotations);
  }
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return "";
  const n = Number(v);
  if (isNaN(n)) return String(v);
  return String(Math.round(n * 100) / 100);
}

function seriestooltip(s: SeriesInfo, row: unknown[]): string {
  if (s.tooltipFn) return s.tooltipFn(row);
  // Default: same as WebAdmin2's valueMethod fallback — just the raw cell value
  return formatValue(row[s.columnIndex]);
}

function seriesValueLabel(s: SeriesInfo, row: unknown[]): string | undefined {
  if (s.valueLabelFn) return s.valueLabelFn(row);
  // Default: no custom label, chart component uses formatChartValue(value)
  return undefined;
}

function renderLineChart(
  rows: unknown[][],
  labels: string[],
  series: SeriesInfo[],
  fill: boolean,
  width: number,
  showLabelsOverride?: boolean,
  annotations?: LineAnnotation[],
): React.ReactElement {
  const lineSeries: LineSeries[] = series.map((s) => ({
    data: rows.map((row, i) => ({
      label: labels[i] ?? "",
      value: toNumber(row[s.columnIndex]),
      tooltip: seriestooltip(s, row),
      displayValue: seriesValueLabel(s, row),
    })),
    color: s.color,
    fill,
  }));
  return <LineChart series={lineSeries} width={width} height={350} showLabels={showLabelsOverride ?? (series.length <= 1)} annotations={annotations} />;
}

function renderBarChart(
  rows: unknown[][],
  labels: string[],
  series: SeriesInfo,
  width: number,
): React.ReactElement {
  const data: BarDatum[] = rows.map((row, i) => ({
    label: labels[i] ?? "",
    value: toNumber(row[series.columnIndex]),
    color: series.color,
    tooltip: seriestooltip(series, row),
    displayValue: seriesValueLabel(series, row),
  }));
  return <BarChart data={data} width={width} height={350} />;
}

function renderStackedBarChart(
  rows: unknown[][],
  labels: string[],
  series: SeriesInfo[],
  width: number,
): React.ReactElement {
  const data: StackedBarDatum[] = rows.map((row, i) => ({
    label: labels[i] ?? "",
    segments: series.map((s) => ({
      value: toNumber(row[s.columnIndex]),
      color: s.color,
      tooltip: seriestooltip(s, row),
    })),
  }));
  return <StackedBarChart data={data} width={width} height={350} />;
}

function renderDonutChart(
  rows: unknown[][],
  labels: string[],
  series: SeriesInfo[],
  innerRadius: number,
): React.ReactElement {
  const s = series[0]!;
  const data: DonutSlice[] = rows.map((row, i) => ({
    label: labels[i] ?? "",
    value: toNumber(row[s.columnIndex]),
    color: getColor(i),
    tooltip: seriestooltip(s, row),
  }));
  return <DonutChart data={data} size={320} innerRadius={innerRadius} showSegmentLabels />;
}
